import { Button } from '@heidi/design-system/button';
import { Textarea } from '@heidi/design-system/textarea';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn, useIsomorphicLayoutEffect } from '@heidi/utils';
import { Pencil } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';

interface ObjectWorkspaceSummaryEditorProps {
  initialSummary: string;
}

export function ObjectWorkspaceSummaryEditor({
  initialSummary,
}: ObjectWorkspaceSummaryEditorProps): JSX.Element {
  const intl = useIntl();
  const [summary, setSummary] = useState(initialSummary);
  const [draft, setDraft] = useState(initialSummary);
  const [isEditing, setIsEditing] = useState(false);
  const editSummaryButtonRef = useRef<HTMLButtonElement>(null);
  const shouldRestoreFocusRef = useRef(false);

  useIsomorphicLayoutEffect(() => {
    if (!isEditing && shouldRestoreFocusRef.current) {
      shouldRestoreFocusRef.current = false;
      editSummaryButtonRef.current?.focus();
    }
  }, [isEditing]);

  const finishEditing = () => {
    shouldRestoreFocusRef.current = true;
    setIsEditing(false);
  };

  const cancelEditing = () => {
    setDraft(summary);
    finishEditing();
  };

  if (!isEditing) {
    return (
      <Button
        ref={editSummaryButtonRef}
        type="button"
        variant="ghost"
        size="sm"
        flush="all"
        className="group h-auto w-full justify-start whitespace-normal text-start text-text-secondary"
        aria-label={intl.formatMessage(messages.editSummary)}
        onClick={() => setIsEditing(true)}
      >
        <span
          className={cn(
            typographyVariant({ variant: 'p2' }),
            'flex-1 whitespace-pre-wrap text-start'
          )}
        >
          {summary}
        </span>
        <Pencil
          className="mt-0.5 size-3.5 shrink-0 text-text-tertiary opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none"
          aria-hidden
        />
      </Button>
    );
  }

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        setSummary(draft.trim());
        finishEditing();
      }}
    >
      <Textarea
        autoFocus
        autoResize
        minRows={3}
        maxRows={7}
        value={draft}
        aria-label={intl.formatMessage(messages.summaryInput)}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            cancelEditing();
          }
        }}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={cancelEditing}>
          {intl.formatMessage(messages.cancel)}
        </Button>
        <Button type="submit" size="sm" disabled={draft.trim() === ''}>
          {intl.formatMessage(messages.save)}
        </Button>
      </div>
    </form>
  );
}
